import axios from '@axios'
import { limparUltimaRota } from '@/velta/utils/ultimaRota'

// Importação dinâmica para evitar dependência circular
// As funções são carregadas apenas quando necessárias
const getPlanPermissionsModule = () => import('@/libs/acl/planPermissions')

/**
 * Troca de empresa dentro da MESMA sessão (acesso de suporte).
 *
 * O logout já limpa estes caches; a impersonação precisa da mesma limpeza
 * porque nenhum deles é separado por empresa: as listagens (SWR em memória) e,
 * principalmente, os contadores de menu — que ficam em localStorage por até 24h
 * e decidem quais itens aparecem. Sem isso o menu do cliente nasceria desenhado
 * com os números da Velta.
 */
const limparCachesDeSessao = async (ctx) => {
    // Permissões de plano também ficam no localStorage e não são por empresa:
    // o `fetchAuthUser` da próxima carga refaz a busca, mas até a resposta
    // chegar valeriam as do plano anterior.
    const planModule = await getPlanPermissionsModule()
    planModule.stopAutoRefresh()
    planModule.clearPlanPermissionsFromStorage()
    const { default: listCache } = await import('@/velta/components/crud/mixins/listCache')
    listCache.clearAll()
    const { limparCache } = await import('@/libs/navegacao/cacheContadores')
    limparCache()
    ctx.commit('navegacao/limpar', null, { root: true })
    limparUltimaRota()
}

export default {
    namespaced: true,
    state: {
        userData: {},
        empresaData: {},
        notifications: [],
        tenantConfig: [],
        // Preenchido pelo fetchAuthUser quando a sessão foi emprestada pelo
        // suporte (ImpersonacaoController). `null` = sessão normal.
        impersonacao: null,
    },
    mutations: {
        setUserData(state, userData) {
            state.userData = userData;
        },
        setTenantConfig(state, tenantConfig) {
            state.tenantConfig = tenantConfig;
        },
        setImpersonacao(state, impersonacao) {
            state.impersonacao = impersonacao || null;
        },
        setEmpresaData(state, empresaData) {
            state.empresaData = empresaData;
        },
        setNotifications(state, notifications) {
            state.notifications = notifications;
        },
        setMarkAllAsReadNotification(state) {
            state.notifications.forEach(notification => {
                notification.lido = 1;
            });
        },
        setMarkAsReadNotificationById(state, id) {
            const notificationIndex = state.notifications.findIndex(notification => notification.id === id);
            if (notificationIndex !== -1) {
                state.notifications[notificationIndex].lido = 1;
            }
        }
    },
    getters: {
        authenticated(state) {
            return Object.keys(JSON.parse(JSON.stringify(state.userData))).length > 0
        },
        tenantConfig(state) {
            return state.tenantConfig
        },
        empresaData(state) {
            return state.empresaData
        },
        impersonacao(state) {
            return state.impersonacao
        },
        abilities(state) {
            // `permissoes` pode faltar: o backend devolve 200 e só loga quando o
            // cálculo de permissões falha (AuthController::fetchAuthUser). Sem o
            // fallback, o spread de `undefined` estoura um TypeError dentro do
            // guard de rota, o `next()` nunca é chamado e o app fica travado no
            // splash — sem barra de endereço, no app das lojas.
            return [
                {
                    subject: 'any'
                },
                ...(state.userData['permissoes'] || [])
            ]
        }
    },
    actions: {
        async logout(ctx) {
            // Limpa permissões do plano e para o refresh automático
            const planModule = await getPlanPermissionsModule()
            planModule.stopAutoRefresh()
            planModule.clearPlanPermissionsFromStorage()
            localStorage.removeItem('veltacar_login_empresa')
            // Sessão encerrada de propósito: a última tela não pode sobreviver
            // para o próximo login (tablet de recepção trocando de operador).
            limparUltimaRota()
            // Cache SWR das listagens não pode vazar entre sessões/usuários
            const { default: listCache } = await import('@/velta/components/crud/mixins/listCache')
            listCache.clearAll()
            // Contadores do menu também são por empresa
            const { limparCache } = await import('@/libs/navegacao/cacheContadores')
            limparCache()
            ctx.commit('navegacao/limpar', null, { root: true })
            ctx.commit('setImpersonacao', null)
            await axios.get('/api/auth/logout').then(() => ctx.commit('setUserData', {})).catch(() => ctx.commit('setUserData', {}))
        },
        async logoutCliente(ctx) {
            localStorage.removeItem('veltacar_login_cliente')
            await axios.get('/api/cliente_geral/logout').then(() => ctx.commit('setUserData', {})).catch(() => ctx.commit('setUserData', {}))
        },
        /**
         * Login central (e-mail + senha, sem CNPJ).
         *
         * Dois desfechos de sucesso, e por isso o `validateStatus` aceita 200
         * além de 201 — antes ele só aceitava 201 e a resposta de escolha de
         * perfil cairia no `.catch` como se fosse erro:
         *
         *   201 -> a conta era uma só; a sessão já existe.
         *          Devolve { autenticado: true, redirect }.
         *   200 -> o e-mail tem 2+ contas. NENHUMA sessão foi criada ainda.
         *          Devolve { autenticado: false, token, perfis } para a tela
         *          de cards decidir.
         */
        login(ctx, data) {
            return axios.get('/sanctum/csrf-cookie').then(() => {
                return axios({
                    method: 'POST',
                    url: '/api/auth/login',
                    validateStatus: (status) => status === 200 || status === 201,
                    data
                }).then(async (response) => {
                    if (response.status === 200 && response.data?.status === 'select_profile') {
                        return {
                            autenticado: false,
                            token: response.data.token,
                            perfis: response.data.perfis || [],
                        }
                    }

                    await ctx.dispatch('fetchAuthUser');
                    return { autenticado: true, redirect: response.data?.redirect || null }
                })
            });
        },
        /**
         * Efetiva a escolha feita na tela de cards. O token é de uso único: se
         * esta chamada falhar, é preciso refazer o login.
         */
        selecionarPerfil(ctx, { token, indice }) {
            return axios.get('/sanctum/csrf-cookie').then(() => {
                return axios({
                    method: 'POST',
                    url: '/api/auth/selecionar-perfil',
                    validateStatus: (status) => status === 201,
                    data: { token, indice },
                }).then((response) => ({ redirect: response.data?.redirect || null }))
            });
        },
        loginCliente(ctx, data) {
            return axios.get('/sanctum/csrf-cookie').then((res) => {
                return axios({
                    method: 'POST',
                    url: '/api/cliente_geral/login',
                    validateStatus: (status) => status === 201,
                    data
                }).then(() => {
                    return ctx.dispatch('fetchAuthUserCliente');
                })
            });
        },
        /**
         * Entra numa empresa como suporte (tela Admin Velta > Empresas).
         *
         * A resposta traz o `redirect`: a volta é por `window.location`, não
         * por rota do Vue Router — a sessão inteira (usuário, empresa,
         * permissões, caches de listagem) mudou no servidor, e recarregar a
         * página é a forma mais barata de não deixar nada do tenant anterior
         * na memória do SPA.
         */
        impersonar(ctx, empresaId) {
            return axios({
                method: 'POST',
                url: '/api/adminvelta/impersonar',
                validateStatus: (status) => status === 201,
                data: { empresa_id: empresaId },
            }).then(async (response) => {
                await limparCachesDeSessao(ctx)
                return response.data
            })
        },
        /**
         * Devolve a sessão para a conta de quem entrou como suporte.
         */
        sairImpersonacao(ctx) {
            return axios({
                method: 'POST',
                url: '/api/adminvelta/impersonar/sair',
                validateStatus: (status) => status === 201,
            }).then(async (response) => {
                ctx.commit('setImpersonacao', null)
                await limparCachesDeSessao(ctx)
                return response.data
            })
        },
        redefinirsenha(ctx, data) {
            return axios.get('/sanctum/csrf-cookie').then((res) => {
                return axios({
                    method: 'POST',
                    url: '/api/auth/redefinirsenha',
                    validateStatus: (status) => status === 201,
                    data
                });
            });
        },
        redefinirSenhaCliente(ctx, data) {
            return axios.get('/sanctum/csrf-cookie').then((res) => {
                return axios({
                    method: 'POST',
                    url: '/api/cliente_geral/redefinirSenha',
                    validateStatus: (status) => status === 201,
                    data
                });
            });
        },
        async fetchAuthUser(ctx) {
            return await axios({
                method: 'GET',
                url: '/api/auth/fetchAuthUser',
                validateStatus: (status) => status === 200
            }).then(async (response) => {
                ctx.commit('setUserData', response.data.userData);
                ctx.commit('setEmpresaData', response.data.empresaData)
                ctx.commit('setNotifications', response.data.notifications);
                ctx.commit('setTenantConfig', response.data.tenantConfig);
                ctx.commit('setImpersonacao', response.data.impersonacao);

                // Inicializa permissões do plano no localStorage e inicia auto-refresh
                const planModule = await getPlanPermissionsModule()
                await planModule.initPlanPermissions()
            }).catch(async (error) => {
                ctx.commit('setUserData', {});
                ctx.commit('setEmpresaData', {});
                ctx.commit('setNotifications', {});
                ctx.commit('setTenantConfig', {});
                ctx.commit('setImpersonacao', null);
                // Limpa permissões do plano em caso de erro
                try {
                    const planModule = await getPlanPermissionsModule()
                    planModule.stopAutoRefresh()
                    planModule.clearPlanPermissionsFromStorage()
                } catch (e) {
                    // Ignora erro se módulo não carregar
                }
            });
        },
        /**
         * Força o recálculo das permissões no backend (sem usar cache).
         * Usado para redundância quando o frontend detecta bloqueio por plano.
         * @returns {Promise<boolean>} - true se as permissões foram atualizadas, false caso contrário
         */
        async refreshPermissions(ctx) {
            try {
                const response = await axios({
                    method: 'GET',
                    url: '/api/minhaspermissoes/refresh',
                    validateStatus: (status) => status === 201
                });

                if (response.data && response.data.arrPermissoes) {
                    const currentUserData = ctx.state.userData;
                    const oldPermissions = JSON.stringify(currentUserData.permissoes || []);
                    const newPermissions = JSON.stringify(response.data.arrPermissoes);

                    // Se as permissões mudaram, atualiza o estado
                    if (oldPermissions !== newPermissions) {
                        ctx.commit('setUserData', {
                            ...currentUserData,
                            permissoes: response.data.arrPermissoes
                        });
                        return true; // Permissões foram atualizadas
                    }
                }
                return false; // Permissões não mudaram
            } catch (error) {
                console.error('Erro ao atualizar permissões:', error);
                return false;
            }
        },
        async afterUpdateOrCreate(ctx, data) {
            return await axios({
                method: 'POST',
                url: '/api/usuario/afterUpdateOrCreate',
                validateStatus: (status) => status === 200,
                data,
            })
        },
        async fetchAuthUserCliente(ctx) {
            return await axios({
                method: 'GET',
                url: '/api/cliente_geral/fetchAuthUser',
                validateStatus: (status) => status === 200
            }).then((response) => {
                ctx.commit('setUserData', response.data.userData);
                ctx.commit('setNotifications', response.data.notifications);
            }).catch((error) => {
                ctx.commit('setUserData', {});
                ctx.commit('setNotifications', {});
            });
        },
        markAllAsRead(ctx) {
            ctx.commit('setMarkAllAsReadNotification');
        },
        markNotificationAsRead(ctx, id) {
            ctx.commit('setMarkAsReadNotificationById', id);
        },
        /**
         * Força atualização das permissões do plano do backend
         * @returns {Promise<Object|null>}
         */
        async refreshPlanPermissions() {
            const planModule = await getPlanPermissionsModule()
            return await planModule.fetchPlanPermissionsFromBackend()
        },
        /**
         * Obtém as permissões do plano do localStorage
         * @returns {Object|null}
         */
        async getPlanPermissions() {
            const planModule = await getPlanPermissionsModule()
            return planModule.getPlanPermissionsFromStorage()
        },
        /**
         * Reinicia o auto-refresh das permissões do plano
         * Útil quando o usuário volta para a aplicação após tempo inativo
         */
        async restartPlanPermissionsAutoRefresh() {
            const planModule = await getPlanPermissionsModule()
            planModule.startAutoRefresh()
        }
    }
}
